import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowRight, 
  Cloud, 
  Code2, 
  BarChart3, 
  ClipboardList, 
  Brain, 
  Bot,
  CheckCircle2,
  MessageSquare,
  Lightbulb,
  RefreshCw,
  Rocket,
  Users,
  Zap
} from 'lucide-react';
import ScheduleMeetingForm from '../components/forms/ScheduleMeetingForm';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMAIL = 'support@zendatasolutions.net';

const SERVICES = [
  { id: 'cloud', title: 'Cloud Services', description: 'With deep expertise in cloud migrations for Azure, AWS & Google Cloud, our implementations save you time and money.', link: '/services#cloud' },
  { id: 'appdev', title: 'Application Development', description: 'We help design and modernize your applications using microservices architecture with state-of-the-art technology.', link: '/services#appdev' },
  { id: 'data', title: 'Data & Analytics', description: 'Deep expertise in data warehousing, data lakes, big data, dashboards, and applied intelligence.', link: '/services#data' },
  { id: 'pm', title: 'Project Management', description: 'Our professionals help mitigate risks with effective communication and collaborative practices.', link: '/services#pm' },
  { id: 'ai', title: 'AI & Machine Learning', description: 'From Image Classifiers to NLP techniques, we build predictive models that drive value.', link: '/services#ai' },
  { id: 'rpa', title: 'RPA', description: 'Automating business processes to save time and drive operational efficiency.', link: '/services#rpa' },
];

const APPROACH_STEPS = [
  { icon: <Users className="w-6 h-6" />, title: 'Collaborative Workshop', description: 'Discover & align goals' },
  { icon: <Lightbulb className="w-6 h-6" />, title: 'Prototype', description: 'Build rapid solutions' },
  { icon: <RefreshCw className="w-6 h-6" />, title: 'Feedback', description: 'Test & refine' },
  { icon: <Zap className="w-6 h-6" />, title: 'Improve', description: 'Iterate & enhance' },
  { icon: <Rocket className="w-6 h-6" />, title: 'Innovate', description: 'Scale & deploy' },
];

const BENEFITS = [
  'Quick Iterations to understand process',
  'Added Focus on Core Features & Experience',
  'Backed by Hypothesis Testing with Data',
  'Generate Key Customer Journeys',
  'Increased Collaboration and Feedback',
  'Reduced Project Risk',
  'Rapid Deployment & First to Market',
];

const ServiceIcon = ({ id }) => {
  const iconClass = "w-6 h-6";
  switch (id) {
    case 'cloud': return <Cloud className={iconClass} />;
    case 'appdev': return <Code2 className={iconClass} />;
    case 'data': return <BarChart3 className={iconClass} />;
    case 'pm': return <ClipboardList className={iconClass} />;
    case 'ai': return <Brain className={iconClass} />;
    case 'rpa': return <Bot className={iconClass} />;
    default: return <Cloud className={iconClass} />;
  }
};

const Home = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        inquiry_type: 'general',
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <p className="text-sm font-medium tracking-widest uppercase text-blue-600 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Helping your business model thrive
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Adaptable, Innovative, Purpose-Driven Solutions
            </h1>
            <p className="text-lg md:text-xl text-slate-500 leading-relaxed mb-8 max-w-3xl">
              Helping your business model thrive in one of the most transformational times in human history.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-base btn-click"
                    data-testid="hero-schedule-meeting-btn"
                  >
                    Schedule a meeting
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                      Schedule a Meeting
                    </DialogTitle>
                    <DialogDescription>
                      Fill out the form and we'll get back to you within 24 hours.
                    </DialogDescription>
                  </DialogHeader>
                  <ScheduleMeetingForm onSuccess={() => setDialogOpen(false)} />
                </DialogContent>
              </Dialog>
              <Link to="/contact">
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-base border-slate-200 hover:bg-slate-50 btn-click"
                  data-testid="hero-contact-btn"
                >
                  Let's Chat
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Strip */}
      <section className="py-6 bg-slate-900" data-testid="value-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-white text-lg md:text-xl font-medium tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
            Data Driven <span className="text-slate-500 mx-3">|</span> Transformative <span className="text-slate-500 mx-3">|</span> Cloud Enabled Solutions
          </p>
        </div>
      </section>

      {/* About Zendata Solutions */}
      <section className="py-20 md:py-28" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-600 text-lg leading-relaxed">
              Zendata Solutions is a Canadian IT consulting organization with a strong focus on Analytics, AI & Digital transformation solutions. We deliver high quality Analytics architectures along with Industry grade, cloud strategy and business transformation practices. In today's fast paced environment, our Industry based solutions will help you simplify your business processes leveraging meaningful Digital Transformation Initiatives.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((service, idx) => (
              <Card 
                key={service.id}
                className="p-6 bg-white border border-slate-100 rounded-xl card-hover group"
                data-testid={`service-card-${idx}`}
              >
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 mb-5 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                  <ServiceIcon id={service.id} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {service.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Learn more
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 md:py-28" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Approach
            </h2>
            <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
              Fail Fast, Win BIG!
            </p>
            <p className="text-slate-500 mt-2">Quick Rapid Prototypes • Time to completion 7-90 days</p>
          </div>

          {/* Process Steps */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-16">
            {APPROACH_STEPS.map((step, idx) => (
              <div key={idx} className="text-center" data-testid={`approach-step-${idx}`}>
                <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center mx-auto mb-4">
                  {step.icon}
                </div>
                <h3 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'var(--font-heading)' }}>
                  {step.title}
                </h3>
                <p className="text-slate-500 text-xs mt-1">{step.description}</p>
              </div>
            ))}
          </div>

          {/* Design Thinking */}
          <div className="bg-slate-50 rounded-2xl p-8 mb-16">
            <h3 className="text-xl font-semibold text-center text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Design Thinking Workshops
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['EMPATHIZE', 'DEFINE', 'IDEATE', 'PROTOTYPE', 'TEST'].map((step, idx) => (
                <div key={idx} className="px-6 py-3 bg-white rounded-full border border-slate-200 text-sm font-medium text-slate-700">
                  {step}
                </div>
              ))}
            </div>
          </div>

          {/* Quote */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <blockquote className="text-2xl md:text-3xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              "You Miss 100% Of The Shots You Don't Take."
            </blockquote>
            <cite className="text-slate-500">— Wayne Gretzky</cite>
            <p className="text-slate-600 mt-6 leading-relaxed">
              A lot of organizations fail to embark on their Digital Transformation journey due to several reasons. Some of them being Cost, Legacy systems, resistance to change. We at Zendata Solutions believe we can alleviate such concerns by keeping costs low. Working with you as part of your team to ensure quality projects and products are delivered on time!
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-white border border-slate-100 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              Clear Benefits of Our Approach
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {BENEFITS.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-blue-600" data-testid="cta-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <p className="text-white text-lg md:text-xl font-medium" style={{ fontFamily: 'var(--font-heading)' }}>
                Request a free discovery session & speak to one of our Transformation Experts
              </p>
            </div>
            <Link to="/contact">
              <Button 
                className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 py-6 text-base whitespace-nowrap btn-click"
                data-testid="cta-lets-chat-btn"
              >
                Let's Chat
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Get in Touch Form */}
      <section className="py-20 md:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Get in Touch
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Ready to start your digital transformation journey? Fill out the form and one of our experts will get back to you within 24 hours.
              </p>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-600 text-sm flex items-center gap-2">
                  <span className="font-semibold">Email:</span>
                  <a href={`mailto:${EMAIL}`} className="text-blue-600 hover:underline">{EMAIL}</a>
                </p>
                <p className="text-slate-600 text-sm flex items-center gap-2">
                  <span className="font-semibold">Location:</span>
                  Toronto, Canada
                </p>
              </div>
            </div>
            <div>
              <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="home-contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="home-name">Your Name *</Label>
                    <Input
                      id="home-name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      data-testid="home-form-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="home-email">Email *</Label>
                    <Input
                      id="home-email"
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      data-testid="home-form-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="home-phone">Phone *</Label>
                    <Input
                      id="home-phone"
                      name="phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      data-testid="home-form-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="home-company">Type of Project *</Label>
                    <Input
                      id="home-company"
                      name="company"
                      placeholder="Cloud Migration"
                      value={formData.company}
                      onChange={handleFormChange}
                      required
                      data-testid="home-form-company"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="home-message">Message *</Label>
                  <Textarea
                    id="home-message"
                    name="message"
                    placeholder="Tell us about your project..."
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                    rows={4}
                    data-testid="home-form-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full btn-click"
                  disabled={formLoading}
                  data-testid="home-form-submit"
                >
                  {formLoading ? 'SENDING...' : 'SEND'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
